import { gql } from "@apollo/client";
import { faPlus, faUsers } from "@fortawesome/free-solid-svg-icons";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import Page from "components/common/page/Page";
import { graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { PersonTagFilterInput, PersonTagSortInput, SortEnumType, TagDatalistDocument, TagDatalistQuery, TagDatalistQueryVariables } from "types/graphql";
import { Roles } from "utilities/app-utilities";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import useAppContext from "utilities/hooks/useAppContext";

type ListItemType = NonNullable<TagDatalistQuery['personTags']>['items'][0];

const personTagsDatalistQuery = gql`
query TagDatalist($where: PersonTagFilterInput, $order: [PersonTagSortInput!], $skip: Int, $take: Int) {
    personTags {
      items(where: $where, order: $order, skip: $skip, take: $take) {
        id
        name
      },
      count(where: $where)
    }
  }`;

type PersonTagDatalistPagePropsType = {
}

const PersonTagDatalistPage = (props: PersonTagDatalistPagePropsType) => {
    const context = useAppContext();
    
    const pageActions = [
        {
            icon: faUsers,
            link: AppRoutes.PERSON_HOME,
            name: 'people', 
            title: context.t('pages.people.list')
        },
        {
            icon: faPlus,
            link: AppRoutes.PERSON_TAG_FORM, 
            hide: !context.userHasRole(Roles.PERSON_WRITE), 
            name: 'add', 
            title: context.t('button.add')
        }
    ];

    const datalistConfig: DatalistPropsType<ListItemType, PersonTagFilterInput, PersonTagSortInput> = {
        name: 'person-tag-datalist-page',
        defaultView: 'table',
        itemId(i) {
            return i.id;
        },
        onItemClick: {
            link: {
                url: AppRoutes.PERSON_TAG_VIEW,
                params: i => ({ id: i.id })
            }
        },
        dataSource: {
            request: async params => {
                try {
                    const variables: TagDatalistQueryVariables = {
                        order: params.sort || [{normalizedName: SortEnumType.Asc}],
                        where: params.filters,
                        skip: params.skip,
                        take: params.take
                    };

                    if (params.searchText?.length) {
                        const search: PersonTagFilterInput = {
                            or: [
                                {
                                    name: {
                                        contains: params.searchText
                                    }
                                },
                                {
                                    normalizedName: {
                                        contains: params.searchText.toLowerCase()
                                    }
                                }
                            ]
                        }

                        variables.where = appendFilters(variables.where, search);
                    }
                    
                    const response = await graph<TagDatalistQuery, TagDatalistQueryVariables>({
                        query: TagDatalistDocument,
                        variables
                    });
    
                    if(response.errors) {
                        throw response.errors;
                    }
    
                    return response.data!.personTags as any;
                } catch (error: any) {
                    context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            }
        },
        table: {
            columns: [
                {
                    name: 'id',
                    title: 'fields.id.name',
                    resolve(i) {
                        return i.id
                    }
                },
                {
                    name: 'name',
                    title: 'fields.name.name',
                    resolve(i) {
                        return i.name
                    }
                }
            ]
        }
    }

    return (
        <Page title="pages.people.tag.list" wFull actions={pageActions}>
            <Datalist {...datalistConfig} />
        </Page>
    );
};

export default PersonTagDatalistPage;
